<template>
  <div class="flex flex-col">
    <div class="flex items-center justify-between mb-4">
      <slot />
      <div class="flex items-center gap-2">
        <el-input
          placeholder="Search..."
          prefix-icon="el-icon-search"
          class="w-[250px]"
          clearable
          v-model="queryParams.search"
        />
        <el-button icon="el-icon-refresh" @click="refreshList" />
      </div>
    </div>
    <div
      class="flex-1 flex flex-col rounded-lg border bg-card text-card-foreground shadow-2xs"
    >
      <div class="flex flex-col space-y-1.5 p-6">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-2xl font-semibold leading-none tracking-tight">
              Alert Recipients
            </h3>
            <p class="text-sm text-muted-foreground">
              Manage users who can receive WhatsApp alerts
            </p>
          </div>
          <el-button @click="handleEditModel()" type="primary">
            <span class="flex flex-row items-center">
              <i class="icon-user-plus mr-2 h-4 w-4" />
              Add Recipient
            </span>
          </el-button>
        </div>
      </div>
      <div class="flex-1 flex flex-col p-6 overflow-hidden">
        <div
          class="flex flex-col relative w-full h-full overflow-hidden border bg-card rounded-lg"
        >
          <div
            class="grid items-center font-semibold border-b p-3"
            style="grid-template-columns: 2.5fr 1.5fr 2fr 2fr 0.5fr; gap: 1rem"
          >
            <div>Recipient ID</div>
            <div>Name</div>
            <div>Phone (WhatsApp)</div>
            <div>Created At</div>
            <div>Actions</div>
          </div>
          <div class="flex-1 flex flex-col relative overflow-hidden">
            <SmartInfiniteCursorScroll
              :loadMethod="listNotificationRecipientsV2"
              :pageSize="20"
              listField="recipientList"
              class="absoute inset-0"
              ref="smartInfiniteCursorScrollRef"
              :queryParams="queryParams"
            >
              <template #default="{ item }">
                <div
                  class="grid items-center border-b py-2 px-3"
                  style="
                    grid-template-columns: 2.5fr 1.5fr 2fr 2fr 0.5fr;
                    gap: 1rem;
                  "
                >
                  <div
                    class="max-w-[180px] font-mono truncate"
                    :title="item.recipient_id"
                  >
                    {{ item.recipient_id }}
                  </div>
                  <div class="truncate" :title="item.name">
                    {{ item.name || "--" }}
                  </div>
                  <div
                    class="truncate"
                    :title="getWhatsAppPhone(item.channels)"
                  >
                    {{ getWhatsAppPhone(item.channels) }}
                  </div>

                  <div class="truncate">
                    {{ formatTimestamp(item.created_at) }}
                  </div>
                  <div>
                    <ActionButtons
                      @command="handleCommand"
                      :actions="actions"
                      :item="item"
                    />
                  </div>
                </div>
              </template>
            </SmartInfiniteCursorScroll>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ActionButtons from "@/components/ActionButtons.vue";
import {
  deleteNotificationRecipientV2,
  testNotificationWhatsAppV2,
  listNotificationRecipientsV2,
} from "@/api";
import SmartInfiniteCursorScroll from "./SmartInfiniteCursorScroll.vue";
import { formatTimestamp } from "@/utils/timeUtil.js";

export default {
  name: "RecipientsView",
  components: {
    ActionButtons,
    SmartInfiniteCursorScroll,
  },
  data() {
    return {
      queryParams: {
        search: "",
      },
      actions: [
        {
          type: "dropdown",
          label: "more",
          icon: "el-icon-more",
          command: "more",
          items: [
            { label: "Edit", command: "edit", icon: "el-icon-edit" },
            {
              label: "Send Test Message",
              command: "test",
              icon: "el-icon-s-promotion",
            },
            {
              label: "Remove",
              command: "delete",
              icon: "el-icon-delete",
              color: "#F56C6C",
              divided: true,
            },
          ],
        },
      ],
    };
  },
  methods: {
    deleteNotificationRecipientV2,
    testNotificationWhatsAppV2,
    listNotificationRecipientsV2,
    formatTimestamp,
    getWhatsAppPhone(channels) {
      if (!channels || !channels.length) return "--";
      let whatsappChannels = "";
      for (const c of channels) {
        if (c && c.config) {
          whatsappChannels +=
            Object.entries(c.config)
              .map(([key, value]) => `${key}: ${value}`)
              .join(", ") + ", ";
        }
      }
      whatsappChannels = whatsappChannels.replace(/, $/, "");
      return whatsappChannels;
    },
    async handleCommand({ command, item }) {
      if (command === "edit") {
        await this.handleEditModel(item);
      } else if (command === "delete") {
        await this.handleDelete(item);
      } else if (command === "test") {
        await this.handleTest(item);
      }
    },
    async refreshList() {
      await this.$refs.smartInfiniteCursorScrollRef.refresh();
    },
    async handleEditModel(item) {
      await this.$loaderWindow({
        component: () => import("./EditRecipient.vue"),
        width: "400px",
        showHeader: false,
        padding: false,
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: {
          id: item?.recipient_id,
          title: item ? `Edit Recipient` : `Add New Recipient`,
        },
      });
      await this.refreshList();
    },
    async handleDelete(item) {
      await this.$confirm(
        `Are you sure you want to delete recipient?`,
        "Warning",
        {
          type: "warning",
        }
      );
      await deleteNotificationRecipientV2(item.recipient_id);
      this.$message.success("Recipient deleted successfully.");
      await this.refreshList();
    },
    async handleTest(item) {
      if (!item || !item.recipient_id) {
        this.$message.error("Recipient ID is missing.");
        return;
      }
      const result = await this.$prompt(
        "Enter the test message to send:",
        "Send Test Message",
        {
          confirmButtonText: "Send",
          cancelButtonText: "Cancel",
          inputPlaceholder: "Type your message here",
          inputValue:
            "This is a test alert from ShineUnion." +
            new Date().toLocaleString(),
        }
      );
      const loading = this.$loading.service({
        lock: true,
        text: "Sending test message...",
        background: "rgba(0, 0, 0, 0.4)",
      });
      try {
        const response = await testNotificationWhatsAppV2({
          phone:
            item.channels &&
            item.channels.whatsapp &&
            item.channels.whatsapp.phone,
          message: result.value,
        });
        await this.$message.alert(
          `provider_msg_id:${response && response.provider_msg_id}`,
          "Test Message Result",
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: "OK",
          }
        );
      } catch (error) {
        const message =
          (error && error.message) ||
          (error && error.msg) ||
          "Failed to send test message.";
        this.$message.error(message);
      } finally {
        loading.close();
      }
    },
  },
};
</script>
